import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, FormControl, FormLabel, Input, List, ListItem, Typography } from '@mui/joy';

const formatVendorLabel = (vendor) => {
  if (!vendor) return '';
  return vendor.mobile ? `${vendor.name} — ${vendor.mobile}` : vendor.name;
};

/**
 * Searchable vendor dropdown.
 * value = vendor_id string; onChange(vendorId) with '' when cleared.
 */
const VendorSelect = ({
  vendors = [],
  value,
  onChange,
  onAddNew,
  loading = false,
  required = false,
  allowEmpty = false,
  label = 'Vendor',
  placeholder = 'Search or select vendor...',
  sx
}) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const containerRef = useRef(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return vendors;
    return vendors.filter((v) => {
      const name = (v.name || '').toLowerCase();
      const mobile = (v.mobile || '').toLowerCase();
      const email = (v.email || '').toLowerCase();
      return name.includes(term) || mobile.includes(term) || email.includes(term);
    });
  }, [vendors, search]);

  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
      return;
    }
    const match = vendors.find((v) => String(v.id) === String(value));
    if (match) setSelectedLabel(formatVendorLabel(match));
  }, [value, vendors]);

  const handleBlur = () => {
    setTimeout(() => setOpen(false), 200);
  };

  const handleSelect = (vendorId, labelText) => {
    if (vendorId === '__add_new__') {
      onAddNew?.();
      setOpen(false);
      setSearch('');
      return;
    }
    onChange(vendorId != null ? String(vendorId) : '');
    setSelectedLabel(labelText || '');
    setSearch('');
    setOpen(false);
  };

  const displayValue = open
    ? search
    : (selectedLabel || ((value && formatVendorLabel(vendors.find((v) => String(v.id) === String(value)))) || ''));

  return (
    <FormControl sx={{ mb: 2, position: 'relative', ...sx }} ref={containerRef}>
      <FormLabel>{label}{required ? ' *' : ''}</FormLabel>
      <Input
        value={displayValue}
        onChange={(e) => {
          setSearch(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={handleBlur}
        placeholder={loading ? 'Loading vendors...' : placeholder}
        disabled={loading}
        required={required}
        sx={{
          '&:focus-within': { borderColor: 'primary.500' }
        }}
      />
      {open && !loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            mt: 0.5,
            maxHeight: 220,
            overflow: 'auto',
            bgcolor: 'background.surface',
            border: '1px solid',
            borderColor: 'neutral.outlinedBorder',
            borderRadius: 'sm',
            boxShadow: 'md',
            zIndex: 1100
          }}
        >
          <List size="md">
            {allowEmpty && (
              <ListItem
                onClick={() => handleSelect('', '')}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(29, 27, 77, 0.1)' },
                  ...(!value ? { bgcolor: 'rgba(29, 27, 77, 0.12)' } : {})
                }}
              >
                <Typography level="body-sm" color="neutral">None</Typography>
              </ListItem>
            )}
            {filtered.length === 0 && (
              <ListItem>
                <Typography level="body-sm" color="neutral">No vendors found</Typography>
              </ListItem>
            )}
            {filtered.map((v) => (
              <ListItem
                key={v.id}
                onClick={() => handleSelect(v.id, formatVendorLabel(v))}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(29, 27, 77, 0.1)' },
                  ...(String(v.id) === String(value) ? { bgcolor: 'rgba(29, 27, 77, 0.12)' } : {})
                }}
              >
                <Box>
                  <Typography level="body-sm" fontWeight={500}>{v.name}</Typography>
                  {(v.mobile || v.email) && (
                    <Typography level="body-xs" color="neutral">
                      {[v.mobile, v.email].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
              </ListItem>
            ))}
            {onAddNew && (
              <ListItem
                onClick={() => handleSelect('__add_new__')}
                sx={{
                  cursor: 'pointer',
                  borderTop: '1px solid',
                  borderColor: 'neutral.outlinedBorder',
                  '&:hover': { bgcolor: 'rgba(29, 27, 77, 0.08)' }
                }}
              >
                <Typography level="body-sm" sx={{ color: 'primary.500', fontWeight: 500 }}>
                  + Add new vendor
                </Typography>
              </ListItem>
            )}
          </List>
        </Box>
      )}
    </FormControl>
  );
};

export default VendorSelect;
